import type { GetWorkspaceResponse } from '@otter-ai/recall-client';
import type { NullableProperties } from '@otter-ai/ts-toolkit';
import { useQuery } from '@tanstack/react-query';
import { workspaceQueryCreator } from '@/query/workspaceQueryCreator';
import type { WorkspaceAdminStatus } from '@/shared/types/common';
import type { PATCHED_Workspace } from '@/shared/types/patch-missing-types';
import { WorkspaceRole } from '@/shared/utils/workspaceRole';

export type WorkspaceHook = {
  /**
   * The current workspace if one exists.
   * If no workspace exists OR the query is loading, this is `null`.
   * Use `hasWorkspace` to check if the user belongs to a workspace.
   */
  workspace: (GetWorkspaceResponse['workspace'] & PATCHED_Workspace) | null;
  /**
   * Workspace permission flags from `getWorkspace`.
   * This is `null` while the query is loading.
   * `isSuperuser`, `isAdmin`, and `isSuperAdmin` are `null` if there is no workspace.
   */
  adminStatus: NullableProperties<WorkspaceAdminStatus> | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  /**
   * Whether the user belongs to a workspace.
   * Basic users are not assigned to a workspace.
   *
   * This is `null` while the query is loading.
   */
  hasWorkspace: boolean | null;
  /**
   * `workspace_role` from `getWorkspace`. The API may send any number; documented
   * values (0–3) are exported as `WorkspaceRole` from `@/shared/utils/workspaceRole`.
   */
  workspaceRole: number | null;
};

/**
 * Get the current workspace - if one exists - and admin status of the current user.
 *
 * @example
 * ```tsx
 * function Example() {
 *   const { workspace, adminStatus, isLoading, hasWorkspace } = useWorkspace();
 *   if (isLoading) {
 *     return <div>Loading...</div>;
 *   }
 *   if (!hasWorkspace) {
 *     return <div>You are not assigned to a workspace. This is a basic account.</div>;
 *   }
 *   if (adminStatus.isSuperuser) {
 *     return <div>You are a superuser of {workspace.name}.</div>;
 *   }
 *   if (adminStatus.isAdmin) {
 *     return <div>You are an admin of {workspace.name}.</div>;
 *   }
 *   return <div>You are a regular user of {workspace.name}.</div>;
 * ```
 */
export function useWorkspace({
  enabled,
}: {
  enabled?: boolean;
} = {}): WorkspaceHook {
  const { data, isLoading, isError, error } = useQuery(
    workspaceQueryCreator({ enabled }),
  );
  const workspace = (data?.workspace ?? null) as
    | (GetWorkspaceResponse['workspace'] & PATCHED_Workspace)
    | null;
  const workspaceRole = data == null ? null : (data.workspace_role ?? null);
  const adminStatus = data
    ? {
        isSuperuser: data.is_superuser ?? null,
        isAdmin: data.is_admin ?? null,
        isSuperAdmin:
          Boolean(workspace?.settings?.enable_super_admin) &&
          workspaceRole === WorkspaceRole.SUPER_ADMIN,
      }
    : null;
  const hasWorkspace = isLoading ? null : Boolean(workspace);
  return {
    workspace,
    adminStatus,
    isLoading,
    isError,
    error,
    hasWorkspace,
    workspaceRole,
  };
}
